import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './ZaloTemplateContent.module.css';

interface ZaloTemplateContentProps {
  templateUrl: string;
  isChatDrawer?: boolean;
  onLoadStart?: () => void;
  onLoadEnd?: () => void;
  onError?: (error: string) => void;
}

export const ZaloTemplateContent: React.FC<ZaloTemplateContentProps> = ({
  templateUrl,
  isChatDrawer = false,
  onLoadStart,
  onLoadEnd,
  onError,
}) => {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [iframeReady, setIframeReady] = useState<boolean>(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const heightAdjustmentTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasAdjustedHeightRef = useRef<boolean>(false);

  const sanitizeHtml = useCallback((html: string): string => {
    try {
      let sanitized = html;

      sanitized = sanitized.replace(/\bvar\s+oaId\s*=.*;/g, '');
      sanitized = sanitized.replace(/\blet\s+oaId\s*=.*;/g, '');
      sanitized = sanitized.replace(/\bconst\s+oaId\s*=.*;/g, '');

      sanitized = sanitized.replace(/\bvar\s+(\w+)\s*=.*;.*\bvar\s+\1\s*=.*;/g, (match) => {
        return match.replace(/\bvar\s+\w+\s*=.*;/, '').trim();
      });

      sanitized = sanitized.replace(
        /<p[^>]*class="[^"]*paybill-text[^"]*"[^>]*>[\s\S]*?<\/p>/g,
        ''
      );

      sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, (match) => {
        if (match.includes('oaId') || match.includes('document.write')) {
          return '';
        }
        return match;
      });

      sanitized = sanitized.replace(/\bdocument\.write\s*\(/g, '// document.write(');

      const enablePaymentScript = `
        <script>
          (function() {
            if (typeof window.enablePaymentIfNeeded === 'undefined') {
              window.enablePaymentIfNeeded = function() {
                /* enablePaymentIfNeeded called but not implemented */
              };
            }
          })();
        </script>
      `;

      if (sanitized.includes('<head>')) {
        sanitized = sanitized.replace('<head>', '<head>' + enablePaymentScript);
      } else if (sanitized.includes('</head>')) {
        sanitized = sanitized.replace('</head>', enablePaymentScript + '</head>');
      } else if (sanitized.includes('<body>')) {
        sanitized = sanitized.replace('<body>', '<body>' + enablePaymentScript);
      } else if (sanitized.includes('</body>')) {
        sanitized = sanitized.replace('</body>', enablePaymentScript + '</body>');
      } else {
        sanitized = enablePaymentScript + sanitized;
      }

      if (sanitized.length < html.length * 0.5) {
        return html;
      }

      return sanitized;
    } catch {
      return html;
    }
  }, []);

  const injectEnablePaymentStub = useCallback((iframe: HTMLIFrameElement) => {
    try {
      const iframeWindow = iframe.contentWindow as Window & { enablePaymentIfNeeded?: () => void };
      if (iframeWindow) {
        iframeWindow.enablePaymentIfNeeded = function() {};

        const doc = iframe.contentDocument;
        if (doc && doc.body) {
          const existingScript = doc.querySelector('script[data-enable-payment-stub]');
          if (!existingScript) {
            const script = doc.createElement('script');
            script.setAttribute('data-enable-payment-stub', 'true');
            script.textContent = 'if(typeof enablePaymentIfNeeded==="undefined"){window.enablePaymentIfNeeded=function(){};}';
            if (doc.head) {
              doc.head.insertBefore(script, doc.head.firstChild);
            } else if (doc.body) {
              doc.body.insertBefore(script, doc.body.firstChild);
            }
          }
        }
      }
    } catch {
        console.error('Error injecting enable payment stub');
    }
  }, []);

  const adjustIframeHeight = useCallback(() => {
    if (!iframeRef.current) return;

    const iframe = iframeRef.current;
    const doc = iframe.contentDocument || iframe.contentWindow?.document;

    if (!doc || !doc.body) return;

    const skeletonHeight = isChatDrawer ? 460 : 550;

    const adjust = () => {
      if (!iframeRef.current || !doc.body) return;

      try {
        const body = doc.body;
        const html = doc.documentElement;

        const styleId = 'template-message-custom-styles';
        if (!doc.getElementById(styleId)) {
          const style = doc.createElement('style');
          style.id = styleId;
          style.textContent = `
            .oam_layout {
              margin-top: 10px !important;
            }
          `;
          doc.head.appendChild(style);
        }

        const scriptId = 'template-message-custom-scripts';
        if (!doc.getElementById(scriptId)) {
          const script = doc.createElement('script');
          script.id = scriptId;
          script.textContent = `
            if (typeof enablePaymentIfNeeded === 'undefined') {
              window.enablePaymentIfNeeded = function() {
                /* enablePaymentIfNeeded called but not implemented */
              };
            }
          `;
          doc.head.appendChild(script);
        }

        body.style.margin = '0';
        body.style.padding = '0';
        body.style.overflow = 'hidden';
        html.style.margin = '0';
        html.style.padding = '0';

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!iframeRef.current || !doc.body) return;

            const contentHeight = Math.min(
              body.scrollHeight,
              body.offsetHeight || body.scrollHeight,
              1200
            );

            iframeRef.current.style.height = `${contentHeight + 20}px`;
            hasAdjustedHeightRef.current = true;
            setIframeReady(true);
            onLoadEnd?.();
          });
        });
      } catch {
        iframe.style.height = `${skeletonHeight}px`;
        hasAdjustedHeightRef.current = true;
        setIframeReady(true);
        onLoadEnd?.();
      }
    };

    if (heightAdjustmentTimeoutRef.current) {
      clearTimeout(heightAdjustmentTimeoutRef.current);
      heightAdjustmentTimeoutRef.current = null;
    }

    heightAdjustmentTimeoutRef.current = setTimeout(adjust, 100);
  }, [isChatDrawer, onLoadEnd]);

  const fetchHtmlContent = useCallback(async () => {
    if (!templateUrl) {
      setError('Template URL is missing');
      setLoading(false);
      onError?.('Template URL is missing');
      return;
    }

    try {
      onLoadStart?.();
      setLoading(true);
      setError('');
      hasAdjustedHeightRef.current = false;

      const response = await fetch(templateUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const html = await response.text();
      const sanitized = sanitizeHtml(html);
      setHtmlContent(sanitized);
      setLoading(false);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load template';
      setError(errorMessage);
      onError?.(errorMessage);
      setLoading(false);
    }
  }, [templateUrl, sanitizeHtml, onLoadStart, onError]);

  const loadIframeContent = useCallback(() => {
    if (!iframeRef.current || !htmlContent) return;

    const iframe = iframeRef.current;
    const skeletonHeight = isChatDrawer ? 460 : 550;

    iframe.style.height = `${skeletonHeight}px`;

    try {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc && iframeDoc.defaultView) {
          (iframeDoc.defaultView as Window & { enablePaymentIfNeeded?: () => void }).enablePaymentIfNeeded = function() {
            /* enablePaymentIfNeeded called but not implemented */
          };
        }
      } catch {
        console.error('Error setting enable payment stub');
      }

      iframe.srcdoc = htmlContent;

      iframe.onload = () => {
        injectEnablePaymentStub(iframe);

        if (heightAdjustmentTimeoutRef.current) {
          clearTimeout(heightAdjustmentTimeoutRef.current);
        }

        heightAdjustmentTimeoutRef.current = setTimeout(() => {
          if (!hasAdjustedHeightRef.current) {
            adjustIframeHeight();
          }
        }, 100);
      };

      heightAdjustmentTimeoutRef.current = setTimeout(() => {
        if (!hasAdjustedHeightRef.current) {
          const doc = iframe.contentDocument || iframe.contentWindow?.document;
          if (!doc || !doc.body || doc.body.innerHTML.trim().length < 100) {
            if (doc) {
              try {
                doc.open();
                doc.write(htmlContent);
                doc.close();
                injectEnablePaymentStub(iframe);
                adjustIframeHeight();
              } catch {
                iframe.style.height = `${skeletonHeight}px`;
                hasAdjustedHeightRef.current = true;
                setIframeReady(true);
                onLoadEnd?.();
              }
            } else {
              iframe.style.height = `${skeletonHeight}px`;
              hasAdjustedHeightRef.current = true;
              setIframeReady(true);
              onLoadEnd?.();
            }
          } else {
            adjustIframeHeight();
          }
        }
      }, 300);
    } catch (err) {
      console.error('Error loading iframe content:', err);
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc) {
          doc.open();
          doc.write(htmlContent);
          doc.close();
          injectEnablePaymentStub(iframe);
          adjustIframeHeight();
        }
      } catch {
        setError('Failed to load template content');
        onError?.('Failed to load template content');
      }
    }
  }, [htmlContent, isChatDrawer, injectEnablePaymentStub, adjustIframeHeight, onLoadEnd, onError]);

  useEffect(() => {
    fetchHtmlContent();
  }, [fetchHtmlContent]);

  useEffect(() => {
    if (htmlContent && !loading && !error) {
      const timer = setTimeout(() => {
        loadIframeContent();
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [htmlContent, loading, error, loadIframeContent]);

  useEffect(() => {
    return () => {
      if (heightAdjustmentTimeoutRef.current) {
        clearTimeout(heightAdjustmentTimeoutRef.current);
      }
    };
  }, []);

  const showSkeleton = (loading || (htmlContent && !iframeReady)) && !error;
  const showIframe = !loading && !error && htmlContent && iframeReady;
  const skeletonHeight = isChatDrawer ? 460 : 550;

  return (
    <div className={styles.templateMessageContainer}>
      <div
        className={styles.templateMessageWrapper}
        style={{
          minHeight: `${skeletonHeight}px`,
        }}
      >
        {showSkeleton && (
          <div
            className={styles.skeletonContainer}
            style={{
              opacity: showIframe ? 0 : 1,
              transition: 'opacity 0.3s ease-in-out',
              pointerEvents: showIframe ? 'none' : 'auto',
              zIndex: showIframe ? 0 : 1,
              minHeight: `${skeletonHeight}px`,
            }}
          >
            <div className={isChatDrawer ? styles.templateSkeletonChatDrawer : styles.templateSkeleton}>
              <div className={styles.skeletonHeader}>
                <div className={`${styles.skeletonLine} ${styles.skeletonLineShort}`} />
                <div className={`${styles.skeletonLine} ${styles.skeletonLineMedium}`} />
              </div>
              <div className={styles.skeletonContent}>
                <div className={`${styles.skeletonLine} ${styles.skeletonLineFull}`} />
                <div className={`${styles.skeletonLine} ${styles.skeletonLineFull}`} />
                <div className={`${styles.skeletonLine} ${styles.skeletonLineMedium}`} />
                <div className={`${styles.skeletonLine} ${styles.skeletonLineFull}`} />
                <div className={`${styles.skeletonLine} ${styles.skeletonLineShort}`} />
              </div>
              <div className={styles.skeletonFooter}>
                <div className={styles.skeletonButton} />
                <div className={styles.skeletonButton} />
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className={styles.templateMessageError}>
            <div className={styles.errorIcon}>⚠️</div>
            <div className={styles.errorText}>
              <strong>Không thể tải nội dung</strong>
              <br />
              <small>{error}</small>
            </div>
          </div>
        )}

        <iframe
          ref={iframeRef}
          className={styles.templateMessageFrame}
          title="Zalo Template"
          sandbox="allow-scripts allow-same-origin allow-forms"
          style={{
            width: '100%',
            border: 'none',
            backgroundColor: 'transparent',
            opacity: showIframe ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
            minHeight: `${skeletonHeight}px`,
            position: 'relative',
            zIndex: showIframe ? 1 : 0,
          }}
        />
      </div>
    </div>
  );
};
